import { useState, useEffect, useCallback } from 'react';
import { heroBanners } from '@/mocks/movies';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const goNext = useCallback(() => {
    goTo((current + 1) % heroBanners.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, [goNext]);

  const banner = heroBanners[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {heroBanners.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-600 ease-in-out ${
            idx === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={item.imageUrl}
            alt={item.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-background-50 z-20"></div>

      <div className="absolute inset-0 z-30 flex items-center">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <span className="px-2.5 py-1 bg-primary-500/90 text-background-950 rounded-md text-xs font-medium">
                {banner.category}
              </span>
              <span className="text-xs text-foreground-300">{banner.year}</span>
              <span className="flex items-center gap-1 text-xs text-primary-400">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-400"></i>
                </span>
                {banner.rating}
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl font-heading text-foreground-100 mb-3 md:mb-4 tracking-wide">
              {banner.title}
            </h2>

            <p className="text-sm md:text-base text-foreground-300 mb-2 md:mb-3 leading-relaxed">
              {banner.subtitle}
            </p>

            <p className="hidden md:block text-sm text-foreground-400 mb-6 leading-relaxed line-clamp-2">
              {banner.description}
            </p>

            <div className="flex items-center gap-3">
              <a
                href={`/article/${banner.id}`}
                className="flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg text-sm md:text-base font-medium whitespace-nowrap transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-fill text-lg"></i>
                </span>
                立即播放
              </a>
              <a
                href={`/article/${banner.id}`}
                className="flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 bg-background-200/40 hover:bg-background-200/60 backdrop-blur-sm text-foreground-200 rounded-lg text-sm md:text-base font-medium whitespace-nowrap transition-colors border border-background-300/30"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-information-line text-lg"></i>
                </span>
                查看详情
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
        {heroBanners.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              idx === current
                ? 'w-6 bg-primary-500'
                : 'w-1.5 bg-background-300/50 hover:bg-background-300'
            }`}
            aria-label={`切换到第 ${idx + 1} 张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + heroBanners.length) % heroBanners.length)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 items-center justify-center rounded-full bg-background-950/40 hover:bg-background-950/60 backdrop-blur-sm text-foreground-300 hover:text-foreground-100 transition-all"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={goNext}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 items-center justify-center rounded-full bg-background-950/40 hover:bg-background-950/60 backdrop-blur-sm text-foreground-300 hover:text-foreground-100 transition-all"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}